import { Check, Divide, Pencil, X } from "lucide-react";
import { useState } from "react";

import { Button } from "../../../components/ui/button";
import { brl } from "../lib/formato";
import { maximoDaParte } from "../lib/partes";

const virgula = (v) => (Number(v) || 0).toFixed(2).replace(".", ",");

// Uma pílula por parte: paga (verde), a da vez (destacada) e as que faltam.
// Parte que falta é clicável: muda o valor dela (dono, 24/09/2026).
function Parte({ indice, valor, pagas, fixa, editando, onEditar }) {
  const paga = indice < pagas;
  const daVez = indice === pagas;
  let visual = "border-border text-muted-foreground";
  if (paga) visual = "border-green-500/40 bg-green-500/10 text-green-700 dark:text-green-400";
  else if (daVez) visual = "border-amber-500 bg-amber-500/10 text-amber-700 dark:text-amber-300 font-bold";
  if (editando) visual += " ring-2 ring-primary";
  const conteudo = (
    <>
      {paga && <Check className="w-3 h-3" />}
      {indice + 1}ª {brl(valor)}
      {!paga && fixa && <Pencil className="w-2.5 h-2.5 opacity-70" />}
    </>
  );
  const classe = `inline-flex items-center gap-1 px-2 py-1 rounded-md border text-xs tabular-nums ${visual}`;
  if (paga || !onEditar) return <span className={classe}>{conteudo}</span>;
  return (
    <button type="button" onClick={() => onEditar(indice)} className={`${classe} hover:border-primary`} title="Mudar o valor desta parte" data-e2e="parte-divisao">
      {conteudo}
    </button>
  );
}

function EditorParte({ divisao, indice, onSalvar, onFechar }) {
  const original = virgula(divisao.valores[indice]);
  const [texto, setTexto] = useState(original);
  const maximo = maximoDaParte(divisao, indice);
  const salvar = () => {
    const num = parseFloat(String(texto).replace(",", "."));
    if (onSalvar(indice, num)) onFechar();
  };
  // Saiu do campo sem OK (ex.: foi direto no Confirmar): aplica, igual ao
  // valor grande — senão cobraria o valor antigo sem avisar. OK/Cancelar do
  // próprio editor tratam o clique deles.
  const aoSair = (e) => {
    if (e.currentTarget.closest("[data-e2e=editor-parte]")?.contains(e.relatedTarget)) return;
    if (texto !== original) salvar();
  };
  return (
    <div className="rounded-lg border bg-background p-2.5 space-y-2" data-e2e="editor-parte">
      <p className="text-xs font-semibold">Quanto a {indice + 1}ª parte vai pagar?</p>
      <div className="flex items-center gap-2">
        <span className="text-sm font-bold text-primary">R$</span>
        <input
          autoFocus
          type="text"
          inputMode="decimal"
          value={texto}
          onFocus={(e) => e.target.select()}
          onChange={(e) => setTexto(e.target.value.replace(/[^\d.,]/g, ""))}
          onKeyDown={(e) => { if (e.key === "Enter") salvar(); if (e.key === "Escape") onFechar(); }}
          onBlur={aoSair}
          className="w-28 h-10 px-2 text-lg font-bold tabular-nums bg-muted rounded-md outline-none focus:ring-2 focus:ring-primary"
          aria-label="Valor da parte"
        />
        <Button type="button" size="sm" className="h-10 font-bold" onClick={salvar}>OK</Button>
        <Button type="button" size="sm" variant="ghost" className="h-10" onClick={onFechar}>Cancelar</Button>
      </div>
      <p className="text-[11px] text-muted-foreground">
        Até {brl(maximo)}. As partes que faltam dividem o resto; as já pagas não mudam.
      </p>
    </div>
  );
}

export default function DivisaoAtiva({ divisao, onCancelar, onEditarParte, rotuloInteira = "Mesa inteira" }) {
  const { partes, pagas, valores, mesaInteira, base, desconto, fixas } = divisao;
  const [editando, setEditando] = useState(null);
  const qtdProdutos = Object.keys(base).length;
  const editavel = !!onEditarParte;
  return (
    <div className="rounded-xl border-2 border-amber-500/50 bg-amber-500/5 p-3 space-y-2.5">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="p-1.5 rounded-md bg-amber-500 text-white shrink-0"><Divide className="w-4 h-4" /></span>
          <div className="min-w-0">
            <p className="text-sm font-bold">Conta dividida em {partes}</p>
            <p className="text-[11px] text-muted-foreground">
              {mesaInteira ? rotuloInteira : `${qtdProdutos} produto(s)`} · {pagas} de {partes} paga(s)
              {desconto && " · com desconto"}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onCancelar}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-red-500 shrink-0"
          title="O que já foi pago continua registrado nos produtos"
        >
          <X className="w-3.5 h-3.5" /> Cancelar divisão
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {valores.map((v, i) => (
          <Parte key={i} indice={i} valor={v} pagas={pagas} fixa={!!fixas?.[i]} editando={editando === i}
            onEditar={editavel ? (k) => setEditando(editando === k ? null : k) : null} />
        ))}
      </div>
      {editavel && editando != null && editando >= pagas && editando < valores.length && (
        <EditorParte key={`${editando}-${valores.join("|")}`} divisao={divisao} indice={editando} onSalvar={onEditarParte} onFechar={() => setEditando(null)} />
      )}
      {editavel && editando == null && pagas < partes && (
        <p className="text-[11px] text-muted-foreground">Toque numa parte para mudar o valor dela.</p>
      )}
    </div>
  );
}
